import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

// Phase 12 Cleanup: `SKILLS_TUTORIAL_CONFIG: ChecklistStep[]` und
// `registerSkillsTutorials(registry)` wurden entfernt (ADR E4 — nicht
// migriert). Aktive Tour-Definitionen leben unten als `TourConfig`.

/**
 * Dialog-Close-Race-Guard für die Skill-Dialog-Steps (skill-icon, skill-
 * departments, save-skill). Mechanismus identisch zu
 * `department-tutorial.config.ts` — `add-edit-skill.component.ts` setzt
 * `document._skillCreatedAt` synchron vor `dialogRef.close()`. Steps mit
 * `attachToDialog` skippen sich, solange der Zeitstempel frisch ist
 * (< 1500 ms).
 */
function isSkillCreatedFresh(): boolean {
  const ts = (document as any)._skillCreatedAt as number | undefined;
  return typeof ts === 'number' && performance.now() - ts < 1500;
}

function skillDialogStepSkipIf(): boolean {
  return !document.querySelector('app-add-edit-skill') || isSkillCreatedFresh();
}

async function waitForSkillDialogGone(): Promise<void> {
  const start = performance.now();
  while (
    document.querySelector('app-add-edit-skill') &&
    performance.now() - start < 400
  ) {
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
  }
}

/**
 * Snapshot der Abteilungs-Auswahlliste am Skill-Dialog via DOM-Attribute.
 * `add-edit-skill.component.html` exposed am `#departments-select`-mat-select:
 *   - `data-department-count`        → Anzahl verfuegbarer Abteilungen
 *   - `data-department-list-loaded`  → true, sobald die Liste geladen ist
 *
 * Wird zur Render-Zeit des `skill-departments`-Steps aufgerufen, um Empty-
 * vs. Populated-Varianten (Tooltip + Voice-Over) zu waehlen. Solange
 * `loaded=false` (initialer Lade-Flicker), bleiben wir auf der neutralen
 * Default-Variante, damit der User nicht kurz den falschen Hinweis sieht.
 * Mechanik identisch zu `readSelectSnapshot` in
 * `department-tutorial.config.ts`.
 */
function departmentsSnapshot(): { count: number; loaded: boolean } {
  const el = document.getElementById('departments-select');
  if (!el) return { count: 0, loaded: false };
  const count = Number(el.getAttribute('data-department-count') ?? '0');
  const loaded = el.getAttribute('data-department-list-loaded') === 'true';
  return { count: Number.isFinite(count) ? count : 0, loaded };
}

/**
 * Kontextuelle In-Dialog-Tour fuer den kleinen Add-Skill-Dialog (aus
 * `AddSkillComponent`, nicht zu verwechseln mit dem grossen Skill-Dialog
 * in `SkillsComponent`). 3 Steps: Name, Icon-Typ, Icon.
 */
/**
 * Welcome-Tour V2: erste Qualifikation anlegen.
 *
 * Fachlich schlanke 6-Step-Struktur:
 *   1. Qualifikationen — Skills-Tab einleiten
 *   2. Qualifikation hinzufügen — Add-Button klicken
 *   3. Name der Qualifikation — Pflichtfeld, onInput
 *   4. Icon auswaehlen — Kategorie + Suche + Grid in einem Step (Pflicht)
 *   5. Abteilungen zuordnen — optional, onChange
 *   6. Speichern
 *
 * Version 4: Icon-Step wurde wieder zu einem kombinierten Step zusammen-
 * gefuehrt, aber mit drei sichtbaren Spotlights (Kategorie, Suche, Grid)
 * und `popoverOutsideDialog`, damit der Popover das Grid nicht verdeckt.
 * Voice-Over-Cues pulsieren synchron zur Erklaerung ueber den drei
 * Bereichen. Die Pflichtauswahl wird per CustomEvent erzwungen:
 * `tour:skill-icon-selected` (dispatcht von `AddEditSkillComponent.
 * onIconSelect()`). Zusaetzlich rendert die Komponente einen roten Border
 * am Grid, wenn der User ohne Icon-Auswahl auf Speichern klickt.
 */
export const ADD_SKILLS_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.AddSkills,
  version: 5,
  title: 'CHECKLIST.ADD_SKILLS.TITLE',
  description: 'CHECKLIST.ADD_SKILLS.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Qualifikationen ist eine Anlege-Tour. Ohne Skill-Create-Recht zeigen
  // wir den Schritt nicht — er fuehrte sonst in einen leeren Dialog.
  requiredPermissions: [['Department.CanCreateSkills']],
  triggers: [
    TourTrigger.FirstVisit,
    TourTrigger.Resume,
    TourTrigger.Manual,
    TourTrigger.AfterSuccess,
  ],
  // AfterSuccess: wird als Nudge angeboten, sobald der User MEHRERE
  // Abteilungen angelegt hat (siehe `minSuccessCount`). Reihenfolge im
  // Funnel: NACH FirstDepartment, damit die Skill-Edit-Dialog-Multi-Selects
  // (Abteilungen-Zuordnung) sinnvoll gefuellt werden koennen. Das
  // Dept-Multi-Select im Skill-Dialog ist optional — wer noch keine
  // Abteilung hat, ueberspringt den Step automatisch (siehe skipIf am
  // 'skill-departments'-Step).
  //
  // `minSuccessCount: 2` (UX-Audit findings/04 #2): Der „Qualifikationen
  // hinzufuegen"-Nudge feuert NICHT unmittelbar nach der ALLERERSTEN
  // Abteilung. Ein realer Betrieb braucht meist mehrere Abteilungen — der
  // Stupser wuerde den User sonst verfrueht aus dem Abteilungs-Schritt
  // ziehen. Erst ab der zweiten Anlage (`department.created` mit
  // `meta.successCount >= 2`) wird der naechste Schritt aktiv angeboten.
  // Davor bleibt er ueber den persistenten Checklist-FAB und die
  // Login-Continuity (`offerNextSetupTour`) jederzeit erreichbar.
  triggerContext: {
    route: '/organization',
    requiresSuccessOf: 'department.created',
    minSuccessCount: 2,
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 168,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  // Priority 30: NACH FirstDepartment (20), VOR FirstEmployee (40). Spiegelt
  // den fachlichen Setup-Funnel: Unternehmen → Abteilung → Qualifikation →
  // Mitarbeiter → Personalbedarf. Reihenfolge ist verbindliche Quelle der
  // Wahrheit, identisch zur mainOrder im Checklist-FAB.
  priority: 30,
  estimatedDurationSec: 240,
  category: 'setup',
  chapters: [
    {
      id: 'main',
      title: 'CHECKLIST.ADD_SKILLS.TITLE',
      steps: [
        {
          id: 'skills-overview',
          anchor: AnchorId.SkillsTab,
          title: 'TUTORIAL.SKILLS.V2.OVERVIEW.TITLE',
          content: 'TUTORIAL.SKILLS.V2.OVERVIEW.CONTENT',
          placement: 'bottom',
          // Zwei Advance-Wege: User kann direkt den Qualifikationen-Tab
          // klicken oder den Weiter-Button nehmen. Beide fuehren zum
          // naechsten Step; der add-skill-Step stellt per onBeforeShow
          // sicher, dass der Tab dann aktiv ist.
          nextCondition: 'onClick',
          showNextButton: true,
        },
        {
          id: 'add-skill',
          // Popover-Anchor = `SkillsTable` (Tabellen-Header, mittig auf
          // der Page) + `placement: 'bottom'` → Popover sitzt mittig
          // unterhalb der Spaltenleiste, gleich weit zu beiden Klick-
          // Zielen. Spotlight bleibt auf beiden CTAs. Begründung siehe
          // department-tutorial Step 2.
          anchor: AnchorId.SkillsTable,
          spotlightAnchor: AnchorId.AddSkillButton,
          additionalSpotlightAnchors: [AnchorId.AddSkillEmptyButton],
          // Tour springt weiter, sobald der Add-Skill-Dialog aufgeht.
          advanceOnDocumentEvent: 'onboarding:skill.add-dialog-opened',
          title: 'TUTORIAL.SKILLS.V2.ADD.TITLE',
          content: 'TUTORIAL.SKILLS.V2.ADD.CONTENT',
          placement: 'bottom',
          showNextButton: false,
          // Falls der User via Weiter-Button kam (statt Tab-Klick), ist der
          // Skills-Tab noch inaktiv und der AddSkillButton existiert nicht
          // im DOM (`getAddButtonId()` haengt am activeTab). Wir aktivieren
          // den Tab programmatisch und warten einen Frame, damit der Anker
          // gemountet ist, bevor der Step aufgeht.
          onBeforeShow: async () => {
            const tab = document.querySelector<HTMLElement>('[onboardingAnchor="skills-tab"]');
            if (tab && !tab.classList.contains('is-active')) {
              tab.click();
              await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
              );
            }
          },
        },
        {
          id: 'skill-name',
          anchor: AnchorId.SkillNameField,
          title: 'TUTORIAL.SKILLS.V2.NAME.TITLE',
          content: 'TUTORIAL.SKILLS.V2.NAME.CONTENT',
          placement: 'bottom',
          nextCondition: 'onInput',
          // Wenn der User direkt aus diesem Step heraus „Speichern" klickt
          // (Skill mit Name + Icon + ohne Departments), dispatcht
          // `add-edit-skill.component.ts` synchron das Event und die Tour
          // springt zum Outro. Steps 4-6 werden via `skipIf` mit Fresh-Flag
          // sauber uebersprungen. Mechanismus spiegelt department-tutorial
          // Step 3.
          advanceOnDocumentEvent: 'onboarding:skill.created',
          // Erster Step im offenen Add-Dialog — Vorgaenger zeigt den
          // "+ Hinzufuegen"-Button auf Page-Ebene, der jetzt vom Dialog
          // verdeckt ist. Zurueck-Sprung waere nicht mehr sichtbar.
          showPrevButton: false,
          // preserveOnClose: Tooltip-X / Esc pausiert NUR die Tour — der
          // Add-Dialog bleibt offen, damit der User seinen bereits getippten
          // Namen nicht verliert (aktiv ausgefuelltes Formular, kein blosser
          // Wizard-Schritt). Resume aus der Checkliste findet den noch offenen
          // Dialog vor und setzt exakt hier fort. Spiegelt department-tutorial.
          dialogContext: { waitForOpen: true, attachToDialog: true, preserveOnClose: true },
        },
        {
          id: 'skill-icon',
          // Kombinierter Pflicht-Step: drei Bereiche des Icon-Panels werden
          // gemeinsam beleuchtet (Kategorie-Dropdown links, Textsuche rechts,
          // Icon-Grid unten). Der Popover wandert per popoverOutsideDialog
          // neben den Dialog, damit er das Grid nicht verdeckt — bei zu
          // schmalem Viewport faellt er automatisch unter den Dialog zurueck.
          // Voice-Over-Cues pulsieren synchron zur Sprache ueber den jeweils
          // erwaehnten Bereich (siehe add-skills.json -> voiceOverCues).
          // Advance erst, wenn der User tatsaechlich ein Icon klickt
          // (Component feuert `tour:skill-icon-selected`).
          anchor: AnchorId.IconSelectionGrid,
          additionalSpotlightAnchors: [
            AnchorId.IconTypeSelect,
            AnchorId.IconSearchField,
          ],
          title: 'TUTORIAL.SKILLS.V2.ICON.TITLE',
          content: 'TUTORIAL.SKILLS.V2.ICON.CONTENT',
          placement: 'right',
          // Doppel-Advance: Icon-Klick im Grid feuert
          // `tour:skill-icon-selected`. Falls der User stattdessen direkt
          // „Erstellen" klickt (z. B. Icon ist via Suche schon ausgewaehlt
          // und Form valide), dispatcht `add-edit-skill` synchron
          // `onboarding:skill.created` → Tour springt zum Outro.
          advanceOnDocumentEvent: [
            'tour:skill-icon-selected',
            'onboarding:skill.created',
          ],
          // Weiter-Button sichtbar, aber konditional disabled. Erst-Durchlauf:
          // kein Icon → Button disabled, der Klick aufs Icon advance't ohnehin
          // automatisch via CustomEvent. Re-Entry von Step 5: Icon ist bereits
          // ausgewaehlt → Button enabled, kein Sackgassen-Effekt. Coordinator
          // liest dazu `data-required-fulfilled="true|false"` am Grid (vom
          // AddEditSkillComponent gesetzt).
          showNextButton: true,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
            preserveOnClose: true,
          },
          // Skip wenn Dialog zu (User hat schon „Speichern" geklickt).
          // Inkl. Fresh-Flag fuer den Race waehrend Dialog-Close-Animation.
          skipIf: skillDialogStepSkipIf,
        },
        {
          id: 'skill-departments',
          anchor: AnchorId.DepartmentsSelect,
          title: 'TUTORIAL.SKILLS.V2.DEPARTMENTS.TITLE',
          // Empty-State-Awareness (analog department-tutorial Step 5
          // `department-skills`): Der Step schaltet zur Render-Zeit zwischen
          // EMPTY (noch keine Abteilung angelegt — nichts zuzuordnen) und
          // POPULATED (Abteilungen existieren) um. Der POPULATED-Text betont,
          // dass die Zuordnung fuer die spaetere Schichtplanung wichtig ist
          // (FixPlaner schlaegt offenen Schichten die passend qualifizierten
          // Mitarbeiter dieser Abteilung vor). Solange die Liste noch laedt
          // (`!loaded`), bleibt es bei der neutralen Basis-Variante, um einen
          // Flacker-Effekt (kurz Empty, dann Populated) zu vermeiden.
          content: () => {
            const snap = departmentsSnapshot();
            if (!snap.loaded) return 'TUTORIAL.SKILLS.V2.DEPARTMENTS.CONTENT';
            return snap.count === 0
              ? 'TUTORIAL.SKILLS.V2.DEPARTMENTS.CONTENT_EMPTY'
              : 'TUTORIAL.SKILLS.V2.DEPARTMENTS.CONTENT_POPULATED';
          },
          contentParams: () => ({ count: departmentsSnapshot().count }),
          // Voice-Over folgt synchron dem Tooltip-Text: Empty-Spur, wenn noch
          // keine Abteilung existiert, sonst die Populated-Spur (betont die
          // Bedeutung fuer die Schichtplanung). Basis-Spur `skill-departments`
          // bleibt der neutrale Fallback, solange die Liste laedt — exakt
          // dieselbe Verzweigung wie bei `content`. Varianten-Spuren sind als
          // eigene Eintraege im Voice-Over-Manifest hinterlegt
          // (scripts/voiceover-manifest.json → add-skills.de:
          // skill-departments-empty / skill-departments-populated).
          audioStepId: () => {
            const snap = departmentsSnapshot();
            if (!snap.loaded) return 'skill-departments';
            return snap.count === 0
              ? 'skill-departments-empty'
              : 'skill-departments-populated';
          },
          placement: 'right',
          nextCondition: 'onChange',
          // Wenn der User aus diesem Step direkt „Erstellen" klickt (Abteilungs-
          // Auswahl ist optional), dispatcht `add-edit-skill` synchron
          // `onboarding:skill.created` → Tour rueckt sofort vor. Ohne diesen
          // Listener wuerde der danach folgende Dialog-Close die Tour ueber
          // den `attachToDialog`-Close-Listener als 'close' beenden.
          advanceOnDocumentEvent: 'onboarding:skill.created',
          optional: true,
          dialogContext: { attachToDialog: true, preserveOnClose: true },
          // Skip NUR, wenn der Dialog bereits zu ist (User hat aus einem
          // frueheren Step „Speichern" geklickt) — inkl. Fresh-Flag fuer den
          // Dialog-Close-Animation-Race. Frueher wurde der Step zusaetzlich bei
          // `data-department-count === 0` uebersprungen; das ist bewusst
          // entfernt, damit der Empty-State seine eigene, erklaerende Variante
          // (Tooltip + Voice-Over) zeigt, statt den User wortlos vorbeizuspulen.
          // Der Step ist `optional` und bietet einen Weiter-Button, sodass der
          // leere (disabled) Dropdown keine Sackgasse ist.
          skipIf: skillDialogStepSkipIf,
        },
        {
          id: 'save-skill',
          anchor: AnchorId.SaveSkillButton,
          title: 'TUTORIAL.SKILLS.V2.SAVE.TITLE',
          content: 'TUTORIAL.SKILLS.V2.SAVE.CONTENT',
          placement: 'left',
          nextCondition: 'onClick',
          // Backup-Advance via Created-Event: onClick auf den Save-Button
          // sollte normalerweise zuerst feuern, aber falls der Klick aus
          // irgendeinem Grund nicht direkt vom Anchor stammt (z. B.
          // Keyboard-Submit), greift der Event-Listener als Sicherheitsnetz.
          advanceOnDocumentEvent: 'onboarding:skill.created',
          showNextButton: false,
          dialogContext: { attachToDialog: true, preserveOnClose: true },
          // Skip wenn Dialog zu (User hat schon aus früherem Step
          // „Speichern" geklickt).
          skipIf: skillDialogStepSkipIf,
        },
        {
          // Abschluss-Step nach erfolgreichem Save: erklaert die Tabelle.
          // Wechselt vom Dialog-Kontext in den Page-Kontext, daher KEIN
          // attachToDialog. Spotlight liegt auf der ganzen Tabelle, fuenf
          // Cues an den Spalten-Headers werden synchron zur Sprache per
          // Pulse hervorgehoben.
          //
          // Placement: `top` + `align: 'start'` haengt den Popover oben
          // LINKS ueber der Tabelle. Begruendung: der Popover ist breit
          // (~340 px) und wuerde im Default 'top center' die Spalten-
          // Header in der Mitte verdecken. Links-aligned verdeckt er nur
          // die Bezeichnung-/Symbol-Spalte — die rechten Spalten (Abteil-
          // ungen, Team, Aktionen), die der Voice-Over am Ende explizit
          // erklaert, bleiben frei sichtbar inkl. Pulse-Ring der Cues.
          //
          // `onBeforeShow` wartet auf das Mounting des Tabellen-Anchors:
          // nach dem onClick auf Speichern schliesst der Dialog mit Material-
          // Animation (~200 ms), die Tabelle hinter dem Backdrop ist erst
          // dann wieder voll sichtbar. Ohne dieses Wait wuerde der Spotlight
          // auf einem noch verdeckten Element landen.
          id: 'skills-table-overview',
          anchor: AnchorId.SkillsTable,
          title: 'TUTORIAL.SKILLS.V2.TABLE_OVERVIEW.TITLE',
          content: 'TUTORIAL.SKILLS.V2.TABLE_OVERVIEW.CONTENT',
          placement: 'top',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          // Page-Step nach Dialog-Close — Vorgaenger (Speichern-Button) lebte
          // im inzwischen geschlossenen Add-Dialog, Zurueck-Sprung waere ohne
          // Anchor.
          showPrevButton: false,
          onBeforeShow: waitForSkillDialogGone,
        },
      ],
    },
  ],
};

export const SKILL_ADD_CONTEXTUAL_TOUR: TourConfig = {
  id: TourFeature.SkillAddContextual,
  version: 2,
  title: 'ADD_SKILL',
  description: 'SKILL_CONTEXTUAL_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 10,
  estimatedDurationSec: 60,
  category: 'contextual-help',
  // UX-Entscheidung (Phase 6 UX-Fix): Das Name-Feld ist selbsterklaerend und
  // raus. Icon-Typ und Icon-Auswahl sind UI-Patterns, die Erklaerung brauchen.
  chapters: [
    {
      id: 'basics',
      title: 'BASIC',
      steps: [
        {
          id: 'icon-type',
          anchor: AnchorId.SkillIconTypeFieldSmall,
          title: 'ICON_TYPE_FIELD_TITLE',
          content: 'EXPLANATION_FOR_ICON_TYPE_FIELD',
          placement: 'left',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'icon',
          anchor: AnchorId.SkillIconFieldSmall,
          title: 'ICON_FIELD_TITLE',
          content: 'EXPLANATION_FOR_ICON_FIELD',
          placement: 'right',
          dialogContext: { attachToDialog: true },
        },
      ],
    },
  ],
};
